// FILE: components/shared/Button.tsx
// Button shared — foundation cho toàn site
// 3 variant: default (phẳng), arcade (3D lún khi tap), jelly (nảy spring)
// Pure CSS, không dep, hydrate-free
"use client";

import { forwardRef, type ButtonHTMLAttributes } from "react";

type Variant = "default" | "arcade" | "jelly";
type Tone = "teal" | "orange" | "slate" | "red" | "amber" | "emerald";
type Size = "sm" | "md" | "lg";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  tone?: Tone;
  size?: Size;
}

/* ── Tone palette: bg + text + border-shadow (cho arcade) ── */
const TONE: Record<Tone, { bg: string; text: string; shadow: string; hover: string }> = {
  teal:     { bg: "bg-teal-600",     text: "text-white",       shadow: "border-teal-800",     hover: "hover:bg-teal-700" },
  orange:   { bg: "bg-orange-500",   text: "text-white",       shadow: "border-orange-700",   hover: "hover:bg-orange-600" },
  slate:    { bg: "bg-slate-100",    text: "text-slate-700",   shadow: "border-slate-300",    hover: "hover:bg-slate-200" },
  red:      { bg: "bg-red-500",      text: "text-white",       shadow: "border-red-700",      hover: "hover:bg-red-600" },
  amber:    { bg: "bg-amber-500",    text: "text-white",       shadow: "border-amber-700",    hover: "hover:bg-amber-600" },
  emerald:  { bg: "bg-emerald-600",  text: "text-white",       shadow: "border-emerald-800",  hover: "hover:bg-emerald-700" },
};

const SIZE: Record<Size, string> = {
  sm: "h-8 px-3 text-[12px] rounded-md",
  md: "h-10 px-4 text-[13px] rounded-lg",
  lg: "h-12 px-6 text-[14px] rounded-xl",
};

const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "default", tone = "teal", size = "md", className = "", children, disabled, ...rest },
  ref
) {
  const t = TONE[tone];
  const base = `inline-flex items-center justify-center gap-1.5 font-bold whitespace-nowrap select-none disabled:opacity-50 disabled:cursor-not-allowed disabled:active:translate-y-0 disabled:active:mt-0`;

  let variantCls = "";
  if (variant === "default") {
    variantCls = `${t.bg} ${t.text} ${t.hover} transition-colors`;
  } else if (variant === "arcade") {
    // 3D effect: border-b-4 tạo "đáy", active dịch xuống 3px + xóa border đáy → cảm giác lún
    variantCls = `${t.bg} ${t.text} ${t.hover} border-b-[3px] ${t.shadow} active:border-b-0 active:translate-y-[3px] active:mt-[3px] transition-[background-color,transform,margin,border] duration-75`;
  } else if (variant === "jelly") {
    // Spring overshoot: cubic-bezier(0.34, 1.56, 0.64, 1) tạo nảy nhẹ khi nhả
    variantCls = `${t.bg} ${t.text} ${t.hover} active:scale-90 transition-transform duration-200 [transition-timing-function:cubic-bezier(0.34,1.56,0.64,1)]`;
  }

  return (
    <button
      ref={ref}
      disabled={disabled}
      className={`${base} ${SIZE[size]} ${variantCls} ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
});

export default Button;
export type { ButtonProps, Variant as ButtonVariant, Tone as ButtonTone, Size as ButtonSize };
